import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import LinkIcon from "@mui/icons-material/Link";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import axios from "axios";
import Swal from "sweetalert2";
import { SDLSUrl } from "../../../config";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import MapDeviceModal from "./MapDeviceModal";
import Header from "../../../components/Header";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  return {
    Authorization: `Bearer ${token.replace(/"/g, "")}`,
    "Content-Type": "application/json",
  };
};

const STATUS_CHIPS = {
  PENDING_CUSTOMER_MAPPING: { label: "Pending Customer", color: "warning" },
  PENDING_LOAN_MAPPING: { label: "Pending Loan", color: "info" },
  MAPPED: { label: "Fully Mapped", color: "success" },
};

const DeviceRegistryTable = () => {
  const theme = useTheme();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Mapping modal
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const query = `
        query AllDeviceRegistries {
          allDeviceRegistries {
            results
            error
            totalCount
          }
        }
      `;
      const response = await axios.post(SDLSUrl.uri, { query }, { headers });
      const { results, error } = response.data?.data?.allDeviceRegistries || {};
      if (error) {
        const parsedErr = JSON.parse(error);
        console.error("Failed to fetch device registry:", parsedErr);
        setDevices([]);
      } else {
        const parsed = results ? JSON.parse(results) : [];
        setDevices(Array.isArray(parsed) ? parsed : []);
      }
    } catch (err) {
      console.error("Failed to fetch device registry:", err);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleSyncFromHonorGuard = async () => {
    setSyncing(true);
    try {
      const headers = getAuthHeaders();
      const mutation = `
        mutation {
          syncDeviceRegistryFromHonorGuard {
            results
            error
          }
        }
      `;
      const response = await axios.post(SDLSUrl.uri, { query: mutation }, { headers });
      const error = response.data?.data?.syncDeviceRegistryFromHonorGuard?.error;
      if (error) {
        const parsedErr = JSON.parse(error);
        Swal.fire("Sync Failed", parsedErr.Error || parsedErr.message || "Failed to sync from Honor Guard.", "error");
      } else {
        await Swal.fire({
          icon: "success",
          title: "Sync Complete!",
          text: "Device list successfully refreshed from Honor Guard.",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchDevices();
      }
    } catch (err) {
      console.error("Sync error:", err);
      Swal.fire("Sync Error", err.message || "Could not complete sync with Honor Guard.", "error");
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenMapModal = (device) => {
    setSelectedDevice(device);
    setMapModalOpen(true);
  };

  // Filtered devices
  const filteredDevices = devices.filter((d) => {
    const matchesStatus = statusFilter === "ALL" || d.MaziwaStatus === statusFilter;
    const q = search.toLowerCase().trim();
    const matchesSearch =
      !q ||
      d.Imei1?.toLowerCase().includes(q) ||
      d.Imei2?.toLowerCase().includes(q) ||
      d.ModelName?.toLowerCase().includes(q) ||
      String(d.CustomerId || "").includes(q) ||
      String(d.LoanId || "").includes(q);
    return matchesStatus && matchesSearch;
  });

  // KPI calculations
  const totalCount = devices.length;
  const pendingCustomerCount = devices.filter((d) => d.MaziwaStatus === "PENDING_CUSTOMER_MAPPING").length;
  const pendingLoanCount = devices.filter((d) => d.MaziwaStatus === "PENDING_LOAN_MAPPING").length;
  const mappedCount = devices.filter((d) => d.MaziwaStatus === "MAPPED").length;

  return (
    <Box m="20px">
      <Header
        title="DEVICE REGISTRY"
        subtitle="Honor Guard Hardware Inventory & Lock Linkage Management"
      />

      {/* KPI Cards Dashboard */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: theme.palette.mode === "dark" ? "#1f2a40" : "#f2f0f0", borderRadius: 2 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <SmartphoneIcon sx={{ fontSize: 38, color: "#1766A2" }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {totalCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Devices
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: theme.palette.mode === "dark" ? "#1f2a40" : "#f2f0f0", borderRadius: 2 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <PendingActionsIcon sx={{ fontSize: 38, color: "#ed6c02" }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  {pendingCustomerCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending Customer
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: theme.palette.mode === "dark" ? "#1f2a40" : "#f2f0f0", borderRadius: 2 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <HourglassEmptyIcon sx={{ fontSize: 38, color: "#0288d1" }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="info.main">
                  {pendingLoanCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending Loan
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: theme.palette.mode === "dark" ? "#1f2a40" : "#f2f0f0", borderRadius: 2 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 38, color: "#2e7d32" }} />
              <Box>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {mappedCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fully Mapped
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search IMEI / Customer / Loan..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />

          <TextField
            select
            size="small"
            label="Linkage Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="ALL">All Statuses</MenuItem>
            <MenuItem value="PENDING_CUSTOMER_MAPPING">Pending Customer</MenuItem>
            <MenuItem value="PENDING_LOAN_MAPPING">Pending Loan</MenuItem>
            <MenuItem value="MAPPED">Fully Mapped</MenuItem>
          </TextField>
        </Box>

        <Box display="flex" gap={1.5} alignItems="center">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={fetchDevices}
            disabled={loading}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncFromHonorGuard}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "Sync from Honor Guard"}
          </Button>
        </Box>
      </Box>

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: theme.palette.mode === "dark" ? "#1f2a40" : "#e0e0e0" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Primary IMEI</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Secondary IMEI</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Linkage Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Customer ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Loan ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Policy</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Seen</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} color="secondary" />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading devices...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredDevices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    No devices found matching current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredDevices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((device) => {
                  const chipProps = STATUS_CHIPS[device.MaziwaStatus] || {
                    label: device.MaziwaStatus || "Unknown",
                    color: "default",
                  };

                  return (
                    <TableRow key={device.Id || device.Imei1} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {device.Imei1}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {device.Imei2 || "—"}
                      </TableCell>
                      <TableCell>{device.ModelName || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={chipProps.label}
                          color={chipProps.color}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {device.CustomerId ? (
                          <Chip
                            label={`#${device.CustomerId}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not Linked
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.LoanId ? (
                          <Chip
                            label={`#${device.LoanId}`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not Linked
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.LastPolicyType ? (
                          <Typography variant="caption" fontWeight={600}>
                            {device.LastPolicyType}
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {device.LastSeen ? new Date(device.LastSeen).toLocaleString() : "Never"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Map to Customer / Loan">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleOpenMapModal(device)}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredDevices.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Map Device Modal */}
      {selectedDevice && (
        <MapDeviceModal
          open={mapModalOpen}
          onClose={() => {
            setMapModalOpen(false);
            setSelectedDevice(null);
          }}
          device={selectedDevice}
          onSuccess={fetchDevices}
        />
      )}
    </Box>
  );
};

export default DeviceRegistryTable;
